import { z } from "zod";

/**
 * What a saved search can filter on.
 *
 * Most of these map straight onto Facebook's own `browse_request_params`,
 * captured from a real session:
 *
 *   commerce_search_and_rp_category_id    []            -> categoryIds
 *   commerce_search_and_rp_condition      null          -> conditions
 *   commerce_search_and_rp_available      true          -> availableOnly
 *   commerce_enable_local_pickup          true          -> localPickup
 *   commerce_enable_shipping              true          -> shipping
 *   filter_price_lower_bound              0             -> priceMinMinor
 *   filter_price_upper_bound              214748364700  -> priceMaxMinor
 *   filter_location_latitude/longitude    51.51/-0.11   -> lat/lng
 *   filter_radius_km                      20            -> radiusKm
 *
 * Letting Facebook apply these is the whole point of the per-alert design:
 * a single query returned 24 candidates 7 minutes old, where crawling the
 * country and filtering locally returned nothing usable in 20 minutes.
 *
 * `excludeKeywords` is ours, not Facebook's, and it is not optional in
 * practice. Measured on a live "sofa under GBP 150 within 40km of London"
 * search: 7 of the first 10 results were carpet-cleaning services, plus a
 * dropshipped "Luxury Sofa - Fast UK Delivery". Facebook's keyword match is
 * looser than a reseller needs, so the product has to subtract.
 */
export const ListingCondition = z.enum([
  "NEW",
  "USED_LIKE_NEW",
  "USED_GOOD",
  "USED_FAIR",
]);
export type ListingCondition = z.infer<typeof ListingCondition>;

export const AlertFilters = z.object({
  /** Free text, passed to Facebook as `params.bqf.query`. */
  query: z.string().min(1).max(200),

  /**
   * Titles containing any of these are dropped after Facebook replies.
   * Matched case-insensitively on word boundaries, so "clean" does not
   * strike out "cleaner-than-new".
   */
  excludeKeywords: z.array(z.string().min(1).max(60)).max(50).default([]),

  /** Empty means every category, which is what the UI should default to. */
  categoryIds: z.array(z.string()).max(30).default([]),

  priceMinMinor: z.number().int().nonnegative().nullable().default(null),
  priceMaxMinor: z.number().int().positive().nullable().default(null),

  /**
   * ISO 4217, for displaying the prices this alert finds.
   *
   * It lives on the alert because Facebook's search payload does not carry
   * one. That was established during the Python implementation -- "the
   * region supplies it at storage time" -- and the TypeScript parser kept
   * reading `listing_price.currency` anyway, which is why every match in
   * production is stored as `currency: null` and the feed renders "110"
   * instead of "£110". An alert is pinned to a lat/lng and a radius, so it
   * is the object in this system that knows which money it is looking at.
   *
   * Defaulted rather than required, and that is what makes this change free
   * of a migration: `filters` is jsonb, so a row written before this key
   * existed still parses and comes back as GBP.
   */
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, "must be a three-letter ISO 4217 code")
    .default("GBP"),

  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  /** Facebook saturates somewhere past 500; the UI should cap well below it. */
  radiusKm: z.number().int().positive().max(500),

  /** Empty means any condition. */
  conditions: z.array(ListingCondition).default([]),

  localPickup: z.boolean().default(true),
  shipping: z.boolean().default(true),
  /** Facebook's `..._rp_available`: hide things already marked sold. */
  availableOnly: z.boolean().default(true),
})
  .refine(
    (f) =>
      f.priceMinMinor === null ||
      f.priceMaxMinor === null ||
      f.priceMinMinor <= f.priceMaxMinor,
    { message: "priceMinMinor must not exceed priceMaxMinor", path: ["priceMinMinor"] },
  );

export type AlertFilters = z.infer<typeof AlertFilters>;

/**
 * How often an alert is allowed to run. The cadence is a plan lever, not a
 * user preference: each run is one Facebook request against a fleet that can
 * only make ~30/minute per identity, so cadence is literally what a customer
 * is paying for.
 */
export const AlertCadence = z.object({
  intervalSeconds: z.number().int().min(30).max(86_400),
});
export type AlertCadence = z.infer<typeof AlertCadence>;

export const Alert = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  name: z.string().min(1).max(120),
  filters: AlertFilters,
  intervalSeconds: z.number().int().min(30).max(86_400),
  active: z.boolean().default(true),
  /**
   * When an alert is created we pull recent history once so the customer sees
   * results immediately instead of an empty screen until something new is
   * posted. Those results are marked as backfill so the UI can say so, and so
   * they never fire a push.
   */
  backfillDays: z.number().int().min(0).max(30).default(7),
  createdAt: z.coerce.date(),
  lastRunAt: z.coerce.date().nullable().default(null),
  nextDueAt: z.coerce.date().nullable().default(null),
});
export type Alert = z.infer<typeof Alert>;

/** What the API accepts when creating one; the server owns the rest. */
export const CreateAlert = Alert.pick({ name: true, intervalSeconds: true })
  .extend({
    filters: AlertFilters,
    backfillDays: z.number().int().min(0).max(30).default(7),
  })
  .partial({ intervalSeconds: true });
export type CreateAlert = z.infer<typeof CreateAlert>;
